/**
 * G-FE — Unified Outbound Voice Settings Console.
 *
 * Single landing surface that consolidates the 5 previously
 * scattered surfaces (Admin → Voice Trunks, Admin → Outbound Voice,
 * Builder voice node, Campaign wizard, Compliance → Audit log) into
 * one console with:
 *
 *   - Drift banner panel (top) — actionable cascade misconfigs
 *     surfaced by the G-BE drift detector
 *   - Status checklist — quick "ready to dispatch?" view
 *   - Tenant Settings section — 10 managed fields, cascade-aware
 *   - Trunks section — links into VoiceTrunksTab for CRUD
 *   - Agents + Campaigns section — voice-enabled inventory
 *   - Audit panel — last 20 voice.* events, last 24h
 *
 * Sticky left nav with 5 anchors so the operator can jump between
 * sections without losing context. Auto-refresh every 30s while
 * tab visible. Reads one /console snapshot per refresh — no fan-out
 * to 5 separate endpoints.
 */
import React, { useEffect, useState, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card } from "../../components/ui";
import { outboundVoiceApi } from "../../api/client";

const NAV_ANCHORS = [
  { id: "status", label: "Status" },
  { id: "settings", label: "Tenant Settings" },
  { id: "trunks", label: "Trunks" },
  { id: "agents", label: "Agents + Campaigns" },
  { id: "audit", label: "Recent Activity" },
];

const SEVERITY_COLORS = {
  error: COLORS.pink || "#ef4444",
  warning: COLORS.orange || "#f59e0b",
  info: COLORS.cyan || "#06b6d4",
};

export default function OutboundVoiceConsole() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await outboundVoiceApi.consoleSnapshot();
      setSnapshot(data?.data || data);
      setError(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 30s while tab visible.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 30_000);
    return () => clearInterval(interval);
  }, [refresh]);

  if (loading && !snapshot) {
    return <div style={{ color: COLORS.textMuted, padding: 20 }}>Loading console…</div>;
  }
  if (error && !snapshot) {
    return <div style={{ color: COLORS.pink || "#ef4444", padding: 20 }}>Failed to load console: {error}</div>;
  }
  if (!snapshot) return null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 20, alignItems: "start" }}>
      <StickyNav />
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <DriftBanner drift={snapshot.drift || []} />
        <StatusChecklist anchorId="status" snapshot={snapshot} />
        <TenantSettings anchorId="settings" settings={snapshot.tenant_settings || {}} />
        <TrunksSection anchorId="trunks" trunks={snapshot.trunks || []} />
        <AgentsCampaigns anchorId="agents" agents={snapshot.agents || []} campaigns={snapshot.campaigns || []} />
        <AuditPanel anchorId="audit" entries={snapshot.audit_tail || []} />
      </div>
    </div>
  );
}

// ─── Sticky left nav ─────────────────────────────────────

function StickyNav() {
  return (
    <nav style={{ position: "sticky", top: 16, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: COLORS.textMuted, letterSpacing: 0.5, marginBottom: 8 }}>
        Outbound Voice Console
      </div>
      {NAV_ANCHORS.map(a => (
        <a
          key={a.id}
          href={`#section-${a.id}`}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 13,
            color: COLORS.text,
            textDecoration: "none",
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          {a.label}
        </a>
      ))}
    </nav>
  );
}

// ─── Drift banner panel ──────────────────────────────────

function DriftBanner({ drift }) {
  if (!drift || drift.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {drift.map((d, i) => {
        const color = SEVERITY_COLORS[d.severity] || COLORS.textMuted;
        return (
          <Card key={i} padding="12px 16px" style={{ borderLeft: `4px solid ${color}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Badge color={color}>{d.severity?.toUpperCase()}</Badge>
              <code style={{ fontSize: 11, color: COLORS.textMuted }}>{d.code}</code>
            </div>
            <div style={{ fontSize: 13, marginTop: 6, color: COLORS.text }}>{d.message}</div>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Status checklist ────────────────────────────────────

function StatusChecklist({ anchorId, snapshot }) {
  const s = snapshot.integration_status || {};
  const items = [
    { key: "livekit_sip_uri", label: "LiveKit SIP URI configured", done: s.livekit_sip_uri_configured },
    { key: "attestation", label: "Attestation completed", done: s.attestation_completed },
    { key: "outbound_enabled", label: "Outbound voice enabled (tenant flag)", done: s.outbound_enabled },
    { key: "trunk", label: `Trunk configured (${s.trunk_count ?? 0})`, done: (s.trunk_count ?? 0) > 0 },
    { key: "agent", label: `Voice agent configured (${s.agent_count ?? 0})`, done: (s.agent_count ?? 0) > 0 },
    { key: "test_phone", label: "Test phone number set (for wet-run)", done: s.test_phone_configured },
  ];
  const allReady = items.every(i => i.done);
  return (
    <Card padding="16px 20px" id={`section-${anchorId}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Ready to dispatch?</div>
        <Badge color={allReady ? (COLORS.green || "#10b981") : (COLORS.orange || "#f59e0b")}>
          {allReady ? "READY" : "INCOMPLETE"}
        </Badge>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {items.map(i => (
          <div key={i.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 18, color: i.done ? (COLORS.green || "#10b981") : COLORS.textMuted, fontWeight: 700 }}>
              {i.done ? "✓" : "○"}
            </span>
            <span style={{ color: i.done ? COLORS.textMuted : COLORS.text }}>{i.label}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: COLORS.textMuted }}>
        Active campaigns: <strong>{s.active_campaign_count ?? 0}</strong>
      </div>
    </Card>
  );
}

// ─── Tenant settings (read-only summary; edit in the dedicated tab) ──

function TenantSettings({ anchorId, settings }) {
  const rows = [
    ["voice_outbound_enabled", "Master switch"],
    ["voice_outbound_daily_spend_cap_usd", "Daily spend cap (USD)"],
    ["voice_outbound_max_concurrent_calls", "Max concurrent calls (tenant)"],
    ["voice_outbound_max_cps", "Max calls per second"],
    ["voice_outbound_max_calls_per_day", "Max calls per day"],
    ["voice_outbound_default_amd_strategy", "Default AMD strategy"],
    ["voice_outbound_default_ring_timeout_s", "Default ring timeout (s)"],
    ["voice_outbound_default_max_call_duration_s", "Default max call duration (s)"],
    ["voice_outbound_default_voicemail_action", "Default voicemail action"],
    ["voice_outbound_test_phone_number", "Test phone number"],
  ];
  return (
    <Card padding="16px 20px" id={`section-${anchorId}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Tenant Settings</div>
        <a href="#admin-outbound-voice-settings" style={{ fontSize: 12, color: COLORS.accent || "#5960b2", textDecoration: "none" }}>
          Edit in Outbound Voice tab →
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
        {rows.map(([key, label]) => {
          const v = settings[key];
          const shown = v === null || v === undefined ? "(not set)" : String(v);
          const muted = v === null || v === undefined;
          return (
            <div key={key} style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px solid ${COLORS.border}`, padding: "4px 0" }}>
              <span style={{ color: COLORS.textMuted }}>{label}</span>
              <span style={{ color: muted ? COLORS.textMuted : COLORS.text, fontFamily: "monospace" }}>{shown}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Trunks section ─────────────────────────────────────

function TrunksSection({ anchorId, trunks }) {
  return (
    <Card padding="16px 20px" id={`section-${anchorId}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Voice Trunks ({trunks.length})</div>
        <a href="#admin-voice-trunks" style={{ fontSize: 12, color: COLORS.accent || "#5960b2", textDecoration: "none" }}>
          Manage in Voice Trunks tab →
        </a>
      </div>
      {trunks.length === 0 ? (
        <div style={{ fontSize: 13, color: COLORS.textMuted, padding: "12px 0" }}>
          No trunks configured. Add one in the Voice Trunks tab before dispatching.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {trunks.map(t => (
            <div key={t.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 100px 80px 80px", gap: 12, padding: "6px 10px", background: COLORS.surface, borderRadius: 6, fontSize: 13, alignItems: "center" }}>
              <span style={{ color: COLORS.text }}>
                {t.name}
                {t.auto_provisioned && <Badge color={COLORS.cyan || "#06b6d4"} style={{ marginLeft: 6 }}>auto</Badge>}
              </span>
              <span style={{ color: COLORS.textMuted, fontFamily: "monospace", fontSize: 12 }}>
                {t.caller_id_e164 || "(no caller ID)"}
              </span>
              <Badge color={t.status === "active" ? (COLORS.green || "#10b981") : t.status === "degraded" ? (COLORS.orange || "#f59e0b") : COLORS.textMuted}>
                {t.status}
              </Badge>
              <span style={{ fontSize: 11, color: COLORS.textMuted, textAlign: "right" }}>
                {t.max_concurrent_calls != null ? `${t.max_concurrent_calls} concurrent` : "—"}
              </span>
              <span style={{ fontSize: 11, color: COLORS.textMuted, textAlign: "right" }}>
                {t.daily_spend_cap_usd != null ? `$${t.daily_spend_cap_usd.toFixed(2)}/d` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Agents + Campaigns section ─────────────────────────

function AgentsCampaigns({ anchorId, agents, campaigns }) {
  return (
    <Card padding="16px 20px" id={`section-${anchorId}`}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Agents + Campaigns</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>Voice-enabled agents ({agents.length})</div>
          {agents.length === 0 ? (
            <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "8px 0" }}>None — flag an agent with voice in the Builder.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {agents.slice(0, 10).map(a => (
                <div key={a.id} style={{ fontSize: 12, padding: "4px 0" }}>{a.name}</div>
              ))}
              {agents.length > 10 && <div style={{ fontSize: 11, color: COLORS.textMuted }}>+ {agents.length - 10} more</div>}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>Active voice campaigns ({campaigns.length})</div>
          {campaigns.length === 0 ? (
            <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "8px 0" }}>None active.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {campaigns.slice(0, 10).map(c => (
                <div key={c.id} style={{ fontSize: 12, padding: "4px 0", display: "flex", justifyContent: "space-between" }}>
                  <span>{c.name}</span>
                  <Badge color={c.status === "running" ? (COLORS.green || "#10b981") : c.status === "paused" ? (COLORS.orange || "#f59e0b") : COLORS.textMuted}>
                    {c.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Audit panel ────────────────────────────────────────

function AuditPanel({ anchorId, entries }) {
  return (
    <Card padding="16px 20px" id={`section-${anchorId}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Recent Activity (last 24h)</div>
        <span style={{ fontSize: 11, color: COLORS.textMuted }}>{entries.length} events</span>
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 13, color: COLORS.textMuted, padding: "12px 0" }}>
          No voice.* audit events in the last 24h.
        </div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {entries.map(e => {
            const color = SEVERITY_COLORS[e.severity] || COLORS.textMuted;
            const ts = e.created_at ? new Date(e.created_at).toLocaleTimeString() : "—";
            return (
              <div key={e.id} style={{ display: "grid", gridTemplateColumns: "80px 90px 1fr", gap: 12, padding: "6px 10px", background: COLORS.surface, borderRadius: 6, fontSize: 12, alignItems: "center" }}>
                <span style={{ color: COLORS.textMuted, fontFamily: "monospace" }}>{ts}</span>
                <Badge color={color}>{e.severity}</Badge>
                <code style={{ fontSize: 11, color: COLORS.text }}>{e.action}</code>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
